import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { ToBillRow, WorkspaceInvoiceRow } from '@jobos/shared';
import { useInvoiceWorkspaceFilters } from './hooks/useInvoiceWorkspaceFilters';
import {
  useInvalidateWorkspace,
  useInvoiceRows,
  useToBillRows,
  useWorkspaceCounts,
} from './hooks/useInvoiceWorkspace';
import { StageTabs } from './components/StageTabs';
import { WorkspaceToolbar } from './components/WorkspaceToolbar';
import { WorkspaceEmptyState } from './components/WorkspaceEmptyState';
import { ToBillCards, ToBillTable } from './components/ToBillTable';
import { CreateInvoiceMenu } from './components/CreateInvoiceMenu';
import { InvoiceStage } from './components/InvoiceStage';

/**
 * The receivables workspace: "who owes us money and what do I need to do today".
 *
 * ONE shell renders four stages. Stage is a filter dimension in the URL rather
 * than four screens, so the date and search filters survive a stage
 * switch — and therefore the stage counts honour the active filters too
 * (spec §6). The tab bar contradicting the list beneath it is the specific
 * failure this shape exists to prevent.
 *
 * Stage 1's rows are jobs and stages 2-4's are invoices. Two row components,
 * one shell — they are not forced into a single type.
 */
export default function InvoiceWorkspace() {
  const navigate = useNavigate();
  const f = useInvoiceWorkspaceFilters();
  const invalidateWorkspace = useInvalidateWorkspace();
  const isToBill = f.stage === 'to_bill';

  const counts = useWorkspaceCounts(f.filters);
  const toBill = useToBillRows(f.filters, f.page);
  const invoices = useInvoiceRows(f.stage, f.filters, f.page);

  const active = isToBill ? toBill : invoices;
  const total = active.data?.total ?? 0;
  const pageSize = active.data?.pageSize ?? 50;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const emptyState = (
    <WorkspaceEmptyState
      stage={f.stage}
      hasFilters={f.activeFilterCount > 0}
      onClearFilters={f.clear}
    />
  );

  return (
    <div className="space-y-4 px-4 py-8 sm:px-6 lg:px-8">
      <div>
        <h1 className="text-2xl font-semibold">Invoices</h1>
        <p className="text-sm text-gray-500">
          Everything waiting to be billed, sent, chased or filed.
        </p>
      </div>

      {/* Filters sit above the tabs, because they apply across every stage. */}
      <WorkspaceToolbar
        dateFrom={f.filters.dateFrom}
        dateTo={f.filters.dateTo}
        onDateFromChange={f.setDateFrom}
        onDateToChange={f.setDateTo}
        search={f.searchInput}
        onSearchChange={f.setSearchInput}
        // An unbilled job has no invoice date, so the server ignores the range
        // on stage 1 — showing the control there would be a lie.
        showDateRange={!isToBill}
        onClear={f.clear}
        activeCount={f.activeFilterCount}
      />

      <StageTabs
        stage={f.stage}
        onStageChange={f.setStage}
        counts={counts.data}
        isLoading={counts.isFetching}
      />

      {active.isPending ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : active.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-800">Could not load this stage.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => active.refetch()}>
            Try again
          </Button>
        </div>
      ) : isToBill ? (
        <ToBillStage
          rows={(toBill.data?.rows ?? []) as ToBillRow[]}
          emptyState={emptyState}
          onRowClick={(row) => navigate(`/jobs/${row.jobId}`)}
          // A new invoice moves the job out of this stage and changes three of
          // the four counts, so the whole workspace is invalidated.
          onCreated={invalidateWorkspace}
        />
      ) : (
        <InvoiceStage
          stage={f.stage}
          rows={(invoices.data?.rows ?? []) as WorkspaceInvoiceRow[]}
          emptyState={emptyState}
          onOpenJob={(row) => navigate(`/jobs/${row.jobId}?tab=billing`)}
        />
      )}

      <Pagination
        page={f.page}
        pageCount={pageCount}
        total={total}
        onPageChange={f.setPage}
        isFetching={active.isFetching}
      />
    </div>
  );
}

/** Responsive split at Tailwind's `lg`, matching JobListTable / JobListCards. */
function ToBillStage({
  rows,
  emptyState,
  onRowClick,
  onCreated,
}: {
  rows: ToBillRow[];
  emptyState: React.ReactNode;
  onRowClick: (row: ToBillRow) => void;
  onCreated: () => void;
}) {
  const actionFor = (row: ToBillRow) => <CreateInvoiceMenu row={row} onCreated={onCreated} />;
  return (
    <>
      <div className="hidden xl:block">
        <ToBillTable
          rows={rows}
          emptyState={emptyState}
          onRowClick={onRowClick}
          actionFor={actionFor}
        />
      </div>
      <div className="xl:hidden">
        <ToBillCards
          rows={rows}
          emptyState={emptyState}
          onRowClick={onRowClick}
          actionFor={actionFor}
        />
      </div>
    </>
  );
}

function Pagination({
  page,
  pageCount,
  total,
  onPageChange,
  isFetching,
}: {
  page: number;
  pageCount: number;
  total: number;
  onPageChange: (page: number) => void;
  isFetching: boolean;
}) {
  if (total === 0) return null;

  return (
    <div className="flex items-center justify-between text-sm text-gray-600">
      <span>
        {total} {total === 1 ? 'row' : 'rows'}
        {isFetching && <span className="ml-2 text-gray-400">updating…</span>}
      </span>
      {pageCount > 1 && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <span className="tabular-nums">
            Page {page} of {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
